import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { track } from "../lib/analytics.js";
import { Section, SectionHeading } from "../components/Section.js";
import { FeatureGrid } from "../components/FeatureGrid.js";
import { FlagshipPreview } from "../components/FlagshipPreview.js";
import { DIFFERENTIATORS } from "../content/differentiators.js";
import { TRUST_STRIP } from "../content/stats.js";
import { PRICING_LINES, PRICING_SUMMARY } from "../content/pricing.js";
import { GAMES } from "../content/games.js";

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Vault Breaker Studios",
  url: "https://vaultbreaker.dev",
  description:
    "New B2B crypto casino game studio. Flagship title Vault Breaker — provably fair, 99.0% RTP, free to integrate, 10% revenue share.",
  sameAs: [],
};

export function Home() {
  const inDev = GAMES.filter((g) => g.status === "in-dev");
  return (
    <>
      <Seo path="/" jsonLd={ORG_JSONLD} />

      {/* HERO */}
      <Section style={{ paddingTop: 80, paddingBottom: 48 }}>
        <div style={{ maxWidth: 820 }}>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(48px, 8vw, 104px)",
              lineHeight: 0.95,
              letterSpacing: "-0.015em",
              margin: 0,
              color: "var(--fg-1)",
              textTransform: "uppercase",
            }}
          >
            Crypto casino originals,
            <br />
            <span style={{ color: "var(--gold-bright)" }}>for 10% of the revenue.</span>
          </h1>
          <p
            style={{
              fontSize: 19,
              color: "var(--fg-2)",
              marginTop: 20,
              maxWidth: 640,
              lineHeight: 1.55,
            }}
          >
            Vault Breaker Studios is a new B2B game studio. Our flagship, Vault Breaker, is a
            30-second provably-fair crash-style original. It's free to integrate. We take 10% of
            the gross gaming revenue it earns you — no setup fees, no monthly minimums.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
            <Link
              to="/casino"
              onClick={() => track("try_demo_click", { surface: "home_hero" })}
              style={{ textDecoration: "none" }}
            >
              <Button variant="gold" style={{ padding: "16px 24px", fontSize: 15 }}>
                TRY DEMO
              </Button>
            </Link>
            <Link
              to="/contact"
              onClick={() => track("contact_sales_click", { surface: "home_hero" })}
              style={{ textDecoration: "none" }}
            >
              <Button style={{ padding: "16px 24px", fontSize: 15 }}>CONTACT SALES</Button>
            </Link>
          </div>
          <div
            style={{
              display: "flex",
              gap: 18,
              marginTop: 32,
              flexWrap: "wrap",
              fontSize: 12,
              color: "var(--fg-3)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {TRUST_STRIP.map((s) => (
              <span key={s}>· {s}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* FLAGSHIP */}
      <Section>
        <SectionHeading eyebrow="Flagship title" title="Vault Breaker" />
        <div style={{ display: "grid", gap: 20, gridTemplateColumns: "minmax(320px, 1.2fr) minmax(260px, 1fr)" }}>
          <FlagshipPreview />
          <Card style={{ display: "grid", gap: 16, padding: 24 }}>
            <h3 style={{ margin: 0, fontSize: 20 }}>Five locks. Thirty seconds.</h3>
            <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
              A multiplier climbs from 1.00× while five vault locks crack at random intervals.
              Cash out at any moment — if all five blow before the player exits, the vault
              seals and the stake is lost. Every round verifiable via SHA-256 seed commit.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontFamily: "var(--font-mono)", fontSize: 12 }}>
              <Stat label="house edge" value="1.0%" />
              <Stat label="RTP" value="99.0%" />
              <Stat label="round length" value="30s" />
              <Stat label="max multiplier" value="1000×" />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Link to="/games/vault-breaker" style={{ textDecoration: "none", flex: 1 }}>
                <Button fullWidth>Game details →</Button>
              </Link>
              <Link to="/play" style={{ textDecoration: "none", flex: 1 }}>
                <Button fullWidth variant="gold">
                  Play
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </Section>

      {/* WHY US */}
      <Section>
        <SectionHeading
          eyebrow="Why us"
          title="Math you can audit. Integration you can finish this week."
          lead="Four things we optimise for, in the order operators care about."
        />
        <FeatureGrid features={DIFFERENTIATORS} />
      </Section>

      {/* PRICING */}
      <Section>
        <SectionHeading
          eyebrow="Pricing"
          title="Free to integrate. 10% of the revenue."
          lead={PRICING_SUMMARY}
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {PRICING_LINES.map((p, i) => (
                <tr
                  key={p.label}
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid var(--line)",
                    background: p.emphasis ? "rgba(212,162,76,0.06)" : "transparent",
                  }}
                >
                  <td
                    style={{
                      padding: "16px 22px",
                      fontFamily: "var(--font-ui)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--fg-3)",
                      width: "55%",
                    }}
                  >
                    {p.label}
                  </td>
                  <td
                    style={{
                      padding: "16px 22px",
                      fontFamily: "var(--font-mono)",
                      fontSize: p.emphasis ? 16 : 14,
                      color: p.emphasis ? "var(--gold-bright)" : "var(--fg-1)",
                      fontWeight: p.emphasis ? 600 : 400,
                      textAlign: "right",
                    }}
                  >
                    {p.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <p
          style={{
            marginTop: 14,
            fontSize: 12,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          · GGR = total bets minus total wins, as settled by the game. Reported monthly.
        </p>
      </Section>

      {/* COMMISSION TEASE — new productized service */}
      <Section>
        <SectionHeading
          eyebrow="New service"
          title="Want a custom game? We'll ship it in a week."
        />
        <Card style={{ display: "grid", gap: 18, padding: 28 }}>
          <p style={{ margin: 0, fontSize: 15, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Tell us the game. We brand one of our templates with your colors, copy, and rules —
            live in 5 days. From $249, plus our usual 10% of revenue. The build fee is a
            commitment deposit, not a license — the same revenue-share contract still applies to
            the deployed game.
          </p>
          <div
            style={{
              display: "flex",
              gap: 18,
              flexWrap: "wrap",
              fontSize: 12,
              color: "var(--fg-3)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <span>· Reskin $249</span>
            <span>· Variant $999</span>
            <span>· Bespoke from $2,999</span>
          </div>
          <div>
            <Link
              to="/commission"
              onClick={() => track("commission_home_tease_click")}
              style={{ textDecoration: "none" }}
            >
              <Button variant="gold">Explore commissions →</Button>
            </Link>
          </div>
        </Card>
      </Section>

      {/* ROADMAP — clearly labelled as future / not built */}
      <Section>
        <SectionHeading
          eyebrow="Pipeline"
          title="Three more originals on the drawing board"
          lead="Concepts we're designing next. None of these are built yet — they share the same engine and RGS surface, so when they ship, adding them to your lobby is a package swap."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {inDev.map((g) => (
            <Card key={g.slug} style={{ display: "grid", gap: 10, position: "relative" }}>
              <Badge kind="fair" style={{ alignSelf: "start", background: "rgba(0,229,255,0.12)", color: "var(--cyan)", borderColor: "rgba(0,229,255,0.35)" }}>
                CONCEPT
              </Badge>
              <h3 style={{ margin: 0, fontSize: 18 }}>{g.name}</h3>
              <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.5 }}>
                {g.description}
              </p>
              <div style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                <span>· {g.roundSeconds}s round</span>
                <span>· {g.rtpPercent}% target RTP</span>
                <span>· {g.category}</span>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* INTEGRATION TEASER */}
      <Section>
        <SectionHeading
          eyebrow="Integration"
          title="Six endpoints. One WebSocket. Done."
          lead="Operators write the adapter in about a week. Here's the core loop — full contract in the integration docs."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "24px 28px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >
{`# 1. open a session
POST /rgs/session/launch   { operatorId, playerId, currency }
  → { sessionId, token, serverSeedHash, balance }

# 2. open a round (debit operator wallet)
POST /rgs/round/open       { sessionId, betAmount }
  → { roundId, nonce, serverSeedHash }

# 3. stream ticks over WS
WS   /game/stream          → tick, crack, resolved

# 4. resolve on cashout (credit operator wallet)
POST /rgs/round/resolve    { sessionId, roundId, cashoutMultiplier }
  → { outcome, cashoutMultiplier, payout, balance }`}
          </pre>
          <div
            style={{
              padding: "14px 28px",
              borderTop: "1px solid var(--line)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "var(--bg-1)",
            }}
          >
            <span className="label">Full contract with schemas and WS events</span>
            <Link to="/integration" style={{ textDecoration: "none" }}>
              <Button>See full contract →</Button>
            </Link>
          </div>
        </Card>
      </Section>

      {/* FINAL CTA */}
      <Section>
        <Card
          style={{
            padding: "48px 32px",
            textAlign: "center",
            background:
              "radial-gradient(ellipse at center, rgba(212,162,76,0.14), transparent 70%), var(--bg-2)",
            border: "1px solid rgba(212,162,76,0.3)",
          }}
        >
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px, 5vw, 44px)",
              lineHeight: 1.05,
              letterSpacing: "0.01em",
              margin: 0,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              maxWidth: 720,
              marginInline: "auto",
            }}
          >
            Try the game.
            <br />
            Keep 90% of what it earns you.
          </h2>
          <p style={{ fontSize: 15, color: "var(--fg-2)", marginTop: 18, marginBottom: 24 }}>
            No setup fee. No license fee. No monthly minimum. Just a cut of the upside.
          </p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <Link
              to="/contact"
              onClick={() => track("contact_sales_click", { surface: "home_final_cta" })}
              style={{ textDecoration: "none" }}
            >
              <Button variant="gold" style={{ padding: "16px 24px", fontSize: 15 }}>
                CONTACT SALES
              </Button>
            </Link>
            <Link
              to="/casino"
              onClick={() => track("try_demo_click", { surface: "home_final_cta" })}
              style={{ textDecoration: "none" }}
            >
              <Button style={{ padding: "16px 24px", fontSize: 15 }}>Try the demo first</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--line)",
        borderRadius: 8,
        padding: "8px 10px",
      }}
    >
      <div style={{ color: "var(--fg-3)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ color: "var(--fg-1)", fontSize: 14, marginTop: 2 }}>{value}</div>
    </div>
  );
}
